import React, { useState, useEffect } from 'react';

/**
 * AddTenantModal Component
 * Modal for creating new tenants
 */
const AddTenantModal = ({ isOpen, onClose, onTenantAdded }) => {
    const [formData, setFormData] = useState({
        name: '',
        domain: '',
        status: 'active',
        isAdmin: false,
        vendorDashboard: '',
        defaultVendorId: ''
    });
    const [vendors, setVendors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Load vendors for the dropdown
    useEffect(() => {
        if (isOpen) {
            loadVendors();
        }
    }, [isOpen]);

    const loadVendors = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch('/api/snowflake/vendors', {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            const data = await response.json();
            if (data.success && data.vendors) {
                setVendors(data.vendors);
            }
        } catch (error) {
            console.error('Error loading vendors:', error);
            setVendors([]);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError(''); // Clear error when user types
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch('/api/admin/tenants', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (data.success) {
                // Reset form
                setFormData({
                    name: '',
                    domain: '',
                    status: 'active',
                    isAdmin: false,
                    vendorDashboard: '',
                    defaultVendorId: ''
                });
                
                // Notify parent component
                if (onTenantAdded) {
                    onTenantAdded(data.tenant);
                }
                
                // Close modal
                onClose();
            } else {
                setError(data.message || 'Failed to create tenant');
            }
        } catch (error) {
            console.error('Error creating tenant:', error);
            setError('Network error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            domain: '',
            status: 'active',
            isAdmin: false,
            vendorDashboard: '',
            defaultVendorId: ''
        });
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '0.5rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                width: '100%',
                maxWidth: '500px',
                margin: '1rem',
                maxHeight: '90vh',
                overflow: 'auto'
            }}>
                {/* Header */}
                <div style={{
                    padding: '1.5rem 1.5rem 1rem 1.5rem',
                    borderBottom: '1px solid #e5e7eb'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h2 style={{
                            fontSize: '1.125rem',
                            fontWeight: '600',
                            color: '#111827',
                            margin: 0
                        }}>
                            Add New Tenant
                        </h2>
                        <button
                            onClick={handleClose}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0.25rem',
                                color: '#6b7280',
                                fontSize: '1.25rem',
                                lineHeight: 1
                            }}
                        >
                            ×
                        </button>
                    </div>
                    <p style={{
                        fontSize: '0.875rem',
                        color: '#6b7280',
                        margin: '0.5rem 0 0 0'
                    }}>
                        Create a new tenant to give them access to the SureCost platform
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
                    {error && (
                        <div style={{
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            color: '#dc2626',
                            padding: '0.75rem',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            marginBottom: '1rem'
                        }}>
                            {error}
                        </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
                        {/* Tenant Name */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '0.25rem'
                            }}>
                                Tenant Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                placeholder="e.g., Acme Corporation"
                                style={{
                                    width: '100%',
                                    padding: '0.5rem 0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                        </div>

                        {/* Domain */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '0.25rem'
                            }}>
                                Domain *
                            </label>
                            <input
                                type="text"
                                name="domain"
                                value={formData.domain}
                                onChange={handleChange}
                                required
                                placeholder="e.g., acme.com"
                                style={{
                                    width: '100%',
                                    padding: '0.5rem 0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            />
                            <p style={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                margin: '0.25rem 0 0 0'
                            }}>
                                Users with this email domain will be assigned to this tenant
                            </p>
                        </div>

                        {/* Status */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '0.25rem'
                            }}>
                                Status
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem 0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    outline: 'none',
                                    backgroundColor: 'white',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="suspended">Suspended</option>
                            </select>
                        </div>

                        {/* Default Vendor Filter */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#374151',
                                marginBottom: '0.25rem'
                            }}>
                                Default Vendor Filter
                            </label>
                            <select
                                name="defaultVendorId"
                                value={formData.defaultVendorId}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem 0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    outline: 'none',
                                    backgroundColor: 'white',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                            >
                                <option value="">All Vendors</option>
                                {vendors.map(vendor => (
                                    <option key={vendor.id} value={vendor.id}>
                                        {vendor.name}
                                    </option>
                                ))}
                            </select>
                            <p style={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                margin: '0.25rem 0 0 0'
                            }}>
                                Default vendor filter when this tenant accesses the dashboard
                            </p>
                        </div>

                        {/* Admin Tenant Checkbox */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="checkbox"
                                name="isAdmin"
                                checked={formData.isAdmin}
                                onChange={handleChange}
                                id="isAdmin"
                                style={{
                                    width: '1rem',
                                    height: '1rem',
                                    accentColor: '#3b82f6'
                                }}
                            />
                            <label htmlFor="isAdmin" style={{
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#374151',
                                cursor: 'pointer'
                            }}>
                                Admin Tenant
                            </label>
                        </div>
                    </div>

                    {/* Actions */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '0.75rem',
                        marginTop: '1.5rem',
                        paddingTop: '1rem',
                        borderTop: '1px solid #e5e7eb'
                    }}>
                        <button
                            type="button"
                            onClick={handleClose}
                            style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#374151',
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                outline: 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: 'white',
                                backgroundColor: isSubmitting ? '#9ca3af' : '#3b82f6',
                                border: 'none',
                                borderRadius: '0.375rem',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                outline: 'none'
                            }}
                            onMouseEnter={(e) => {
                                if (!isSubmitting) {
                                    e.target.style.backgroundColor = '#2563eb';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSubmitting) {
                                    e.target.style.backgroundColor = '#3b82f6';
                                }
                            }}
                        >
                            {isSubmitting ? 'Creating...' : 'Create Tenant'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddTenantModal;
